import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import "@/App.css";
import MorseTree from "@/components/MorseTree";
import { useMorseAudio } from "@/hooks/useMorseAudio";
import { MORSE_TO_CHAR, CHAR_TO_MORSE, pathForMorse } from "@/lib/morse";
import { randomSentence, DIFFICULTY_LABELS, CUSTOM_DIFFICULTY } from "@/lib/practice";
import { Switch } from "@/components/ui/switch";
import { Slider } from "@/components/ui/slider";
import * as SliderPrimitive from "@radix-ui/react-slider";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Popover, PopoverTrigger, PopoverContent } from "@/components/ui/popover";
import { Mic, MicOff, Sun, Moon, Volume2, VolumeX, Radio, Sparkles, Gauge } from "lucide-react";

// PARIS-standard Morse timing. unit = 1200 / WPM.
// Dot = 1 unit, Dash = 3 units, intra-element gap = 1 unit,
// inter-letter gap = 3 units, inter-word gap = 7 units.
function timingForWpm(wpm) {
  const unit = 1200 / Math.max(3, wpm);
  return {
    unit,
    // 2 units = midway between dot and dash
    dotDashMs: unit * 2,
    // Letter break: 3 units. Add small slack so a slightly-sloppy gap
    // doesn't prematurely commit a letter.
    letterMs: unit * 3 + Math.min(120, unit),
    // Word break: 7 units + slack.
    wordMs: unit * 7 + Math.min(200, unit * 1.5),
  };
}

export default function App() {
  const [theme, setTheme] = useState("dark");
  const [showTree, setShowTree] = useState(true);
  const [showLetters, setShowLetters] = useState(true);
  const [audioEnabled, setAudioEnabled] = useState(false);
  const [micEnabled, setMicEnabled] = useState(false);
  const [scribeEnabled, setScribeEnabled] = useState(true);
  const [sensitivity, setSensitivity] = useState(70);
  const [wpm, setWpm] = useState(15);
  const [frequency, setFrequency] = useState(528);
  const [freqText, setFreqText] = useState("528");
  const [transcription, setTranscription] = useState("");
  // Parallel raw-morse stream (e.g. ".- -... ..."), updated alongside
  // transcription. Lets the user inspect the dot/dash detector output
  // directly via the toggle next to the Clear button.
  const [transcriptionMorse, setTranscriptionMorse] = useState("");
  const [transcribeMode, setTranscribeMode] = useState("text"); // "text" | "morse"
  const [currentMorse, setCurrentMorse] = useState("");
  const [isPressed, setIsPressed] = useState(false);
  const [practiceDifficulty, setPracticeDifficulty] = useState(1); // 0 easy, 1 med, 2 hard
  const [practiceSentence, setPracticeSentence] = useState("");
  const [practiceStarted, setPracticeStarted] = useState(false);
  const [calibResult, setCalibResult] = useState(null);
  // When calibration measures real pulses, we override the dot/dash threshold
  // alone (NOT WPM — WPM controls letter/word gap timing which both sides
  // agree on; only the threshold needs to absorb acoustic decay inflation).
  const [dotDashOverride, setDotDashOverride] = useState(null); // ms or null
  // Per-element references captured during guided calibration.
  const [calDot, setCalDot] = useState(null);
  const [calDash, setCalDash] = useState(null);
  // Guided calibration step: null (idle) | "dot" | "dash"
  const [calStep, setCalStep] = useState(null);
  // Light-up state for the indicators on every detected element.
  // (No longer used — the top dot/dash indicators are derived directly
  //  from currentMorse inside MorseTree.)

  const pressStartRef = useRef(0);
  const letterTimerRef = useRef(null);
  const wordTimerRef = useRef(null);
  const wordAlreadyAppendedRef = useRef(false);
  const currentMorseRef = useRef("");
  const lastSourceRef = useRef("manual"); // "manual" | "mic"
  const scribeRef = useRef(scribeEnabled);
  const timingRef = useRef(timingForWpm(15));

  useEffect(() => {
    const t = timingForWpm(wpm);
    t.dotDashBaseMs = t.dotDashMs; // always WPM-derived (for manual presses)
    t.dotDashMicMs =
      dotDashOverride !== null ? dotDashOverride : t.dotDashMs; // override applies only to mic
    timingRef.current = t;
  }, [wpm, dotDashOverride]);

  useEffect(() => {
    scribeRef.current = scribeEnabled;
  }, [scribeEnabled]);

  useEffect(() => {
    currentMorseRef.current = currentMorse;
  }, [currentMorse]);

  useEffect(() => {
    document.documentElement.classList.toggle("dark", theme === "dark");
  }, [theme]);

  const appendChar = useCallback((ch) => {
    setTranscription((t) => t + ch);
    // Mirror to the raw-morse stream: a typed space (word break) becomes
    // " / " (standard inter-word marker). Letter morse is written by
    // finalizeLetter directly, so we don't echo it again here.
    if (ch === " ") setTranscriptionMorse((m) => m + " / ");
  }, []);

  const finalizeLetter = useCallback(() => {
    const morse = currentMorseRef.current;
    if (!morse) return;
    const ch = MORSE_TO_CHAR[morse] || "?";
    setTranscription((t) => t + ch);
    setTranscriptionMorse((m) => (m ? m + " " : "") + morse);
    currentMorseRef.current = "";
    setCurrentMorse("");
  }, []);

  const scheduleTimers = useCallback(() => {
    if (letterTimerRef.current) clearTimeout(letterTimerRef.current);
    if (wordTimerRef.current) clearTimeout(wordTimerRef.current);
    const t = timingRef.current;
    letterTimerRef.current = setTimeout(() => {
      finalizeLetter();
    }, t.letterMs);
    wordAlreadyAppendedRef.current = false;
    wordTimerRef.current = setTimeout(() => {
      if (wordAlreadyAppendedRef.current) return;
      const fromMic = lastSourceRef.current === "mic";
      if (!fromMic || scribeRef.current) {
        appendChar(" ");
      }
      wordAlreadyAppendedRef.current = true;
    }, t.wordMs);
  }, [finalizeLetter, appendChar]);

  const clearTimers = useCallback(() => {
    if (letterTimerRef.current) clearTimeout(letterTimerRef.current);
    if (wordTimerRef.current) clearTimeout(wordTimerRef.current);
    letterTimerRef.current = null;
    wordTimerRef.current = null;
  }, []);

  // Audio hook: tone play/stop + mic detection
  const onToneEdge = useCallback((on, ts) => {
    // Mic-driven edge: mirror press/release behavior, tag source as "mic"
    if (on) {
      handlePressDown(ts, "mic");
    } else {
      handlePressUp(ts);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Track whether the user has manually touched the sensitivity slider.
  // If yes, we respect their setting and skip the auto noise-floor adjust.
  const sensUserTouchedRef = useRef(false);
  const [noiseFloorInfo, setNoiseFloorInfo] = useState(null);
  const handleNoiseFloor = useCallback((info) => {
    setNoiseFloorInfo(info);
    if (!sensUserTouchedRef.current && Number.isFinite(info.suggestedSensitivity)) {
      setSensitivity(info.suggestedSensitivity);
    }
  }, []);

  const audio = useMorseAudio({
    frequency,
    audioEnabled,
    sensitivity,
    onToneEdge,
    onNoiseFloor: handleNoiseFloor,
  });

  const handlePressDown = useCallback(
    (ts = performance.now(), source = "manual") => {
      clearTimers();
      lastSourceRef.current = source;
      pressStartRef.current = ts;
      setIsPressed(true);
      // Only manual presses play the local tone, and they mute the mic
      // detector for the duration (plus a small tail) so we don't decode
      // our own speaker echo.
      if (source === "manual") {
        audio.setTransmitting(true);
        audio.startTone();
      }
    },
    [audio, clearTimers]
  );

  const handlePressUp = useCallback(
    (ts = performance.now()) => {
      if (!pressStartRef.current) return;
      const duration = ts - pressStartRef.current;
      pressStartRef.current = 0;
      setIsPressed(false);
      if (lastSourceRef.current === "manual") {
        audio.stopTone();
        // Keep mic muted for a short tail so the speaker decay doesn't
        // produce a spurious ON edge right after we release.
        audio.setTransmitting(false, 300);
      }
      const srcIsMic = lastSourceRef.current === "mic";
      const effDotDash = srcIsMic
        ? timingRef.current.dotDashMicMs
        : timingRef.current.dotDashBaseMs;
      const sym = duration < effDotDash ? "." : "-";
      currentMorseRef.current = currentMorseRef.current + sym;
      setCurrentMorse(currentMorseRef.current);
      scheduleTimers();
    },
    [audio, scheduleTimers]
  );

  // Mic enable/disable
  useEffect(() => {
    if (micEnabled) {
      // Always re-run the "Auto from noise" measurement when Listen is
      // turned on. Clearing the user-touched flag tells handleNoiseFloor
      // to apply the suggested sensitivity even if the user adjusted it
      // during a previous session.
      sensUserTouchedRef.current = false;
      audio.startMic();
    } else {
      audio.stopMic();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [micEnabled]);

  // Keyboard: Space bar as tap button
  useEffect(() => {
    const down = (e) => {
      if (e.code !== "Space" || e.repeat) return;
      const t = e.target;
      if (t && (t.tagName === "INPUT" || t.tagName === "TEXTAREA")) return;
      e.preventDefault();
      handlePressDown();
    };
    const up = (e) => {
      if (e.code !== "Space") return;
      const t = e.target;
      if (t && (t.tagName === "INPUT" || t.tagName === "TEXTAREA")) return;
      e.preventDefault();
      handlePressUp();
    };
    window.addEventListener("keydown", down);
    window.addEventListener("keyup", up);
    return () => {
      window.removeEventListener("keydown", down);
      window.removeEventListener("keyup", up);
    };
  }, [handlePressDown, handlePressUp]);

  const activePath = useMemo(() => pathForMorse(currentMorse), [currentMorse]);
  // If currently pressed and no morse yet, still light the root to hint the start
  const liveActivePath = isPressed && currentMorse.length === 0 ? ["L0-0"] : activePath;

  const handleFreqSlider = (v) => {
    const n = Math.round(v[0]);
    setFrequency(n);
    setFreqText(String(n));
  };
  const handleFreqInputChange = (e) => {
    const raw = e.target.value;
    setFreqText(raw);
    const v = parseInt(raw, 10);
    if (!Number.isNaN(v) && v >= 50 && v <= 6000) {
      setFrequency(v);
    }
  };
  const commitFreqInput = () => {
    const v = parseInt(freqText, 10);
    const clamped = Number.isNaN(v) ? 528 : Math.max(50, Math.min(6000, v));
    setFrequency(clamped);
    setFreqText(String(clamped));
  };

  // Custom-mode user-typed message. Separate state from practiceSentence
  // so that toggling away from CUSTOM and back doesn't lose what the
  // user typed, and so we can clear the input after a successful send
  // without affecting any previously-generated practice sentence.
  const [customText, setCustomText] = useState("");
  const customInputRef = useRef(null);

  // Practice sentence generation. In CUSTOM mode the GEN button focuses
  // the input instead — there's nothing to randomly generate.
  const handleGenerate = useCallback(() => {
    if (practiceDifficulty === CUSTOM_DIFFICULTY) {
      setPracticeStarted(true);
      // Defer focus to the next frame so the input has actually mounted.
      requestAnimationFrame(() => customInputRef.current?.focus());
      return;
    }
    const next = randomSentence(practiceDifficulty, practiceSentence);
    setPracticeSentence(next);
    setPracticeStarted(true);
  }, [practiceDifficulty, practiceSentence]);

  // Auto-key a morse string (e.g. when user clicks a tree node). Uses the
  // same handlePressDown/handlePressUp path as manual tapping so tree
  // animation + tone + transcription all happen naturally.
  const autoKeyAbortRef = useRef(null);
  const [isAutoKeying, setIsAutoKeying] = useState(false);
  const keyMorseSequence = useCallback(
    async (morse) => {
      // Ignore concurrent triggers while a sequence is already playing.
      if (autoKeyAbortRef.current) return;
      const token = { aborted: false };
      autoKeyAbortRef.current = token;
      setIsAutoKeying(true);

      const unit = timingRef.current.unit;
      const wait = (ms) => new Promise((r) => setTimeout(r, ms));

      try {
        for (let i = 0; i < morse.length; i++) {
          if (token.aborted) return;
          const sym = morse[i];
          const dur = sym === "." ? unit : unit * 3;
          handlePressDown(performance.now(), "manual");
          await wait(dur);
          if (token.aborted) {
            handlePressUp(performance.now());
            return;
          }
          handlePressUp(performance.now());
          if (i < morse.length - 1) {
            await wait(unit); // intra-element gap
          }
        }
      } finally {
        autoKeyAbortRef.current = null;
        setIsAutoKeying(false);
      }
    },
    [handlePressDown, handlePressUp]
  );

  const handleTreeNodeClick = useCallback(
    (node) => {
      if (!node || !node.morse) return;
      keyMorseSequence(node.morse);
    },
    [keyMorseSequence]
  );

  // Auto-key a full text string. Converts each character to its morse,
  // inserts proper inter-element / inter-letter / inter-word gaps, and
  // honors the same abort token so the user can interrupt.
  //
  // Critical timing detail: the per-letter transcription commit happens
  // via `scheduleTimers` after `letterMs` of silence following the last
  // release. Pressing down again cancels that timer, so we MUST wait at
  // least letterMs (+ a small buffer for setTimeout dispatch jitter)
  // before the next letter, otherwise letters silently merge. Same for
  // word boundaries: wordMs must elapse for the space to be appended.
  const keyTextSequence = useCallback(
    async (text) => {
      if (!text) return;
      if (autoKeyAbortRef.current) return;
      const token = { aborted: false };
      autoKeyAbortRef.current = token;
      setIsAutoKeying(true);

      const T = timingRef.current;
      const unit = T.unit;
      const wait = (ms) => new Promise((r) => setTimeout(r, ms));
      // Small buffer so the letter-finalize timeout has time to fire
      // BEFORE we kick off the next letter (which would clear it).
      const COMMIT_BUFFER_MS = 30;
      const upper = text.toUpperCase();

      // Tokenize into [{kind:'char', morse:'...'} | {kind:'space'}] so
      // we can apply the right gap kind after each token.
      const tokens = [];
      for (const ch of upper) {
        if (ch === " ") {
          tokens.push({ kind: "space" });
        } else {
          const m = CHAR_TO_MORSE[ch];
          if (m) tokens.push({ kind: "char", morse: m });
          // unknown punctuation is silently skipped
        }
      }
      // Collapse leading/trailing spaces and consecutive spaces — a
      // double-space would just produce an extra blank in transcription
      // which is rarely what the user intends.
      const compact = [];
      for (const t of tokens) {
        if (t.kind === "space") {
          if (!compact.length) continue;
          if (compact[compact.length - 1].kind === "space") continue;
        }
        compact.push(t);
      }
      while (compact.length && compact[compact.length - 1].kind === "space") {
        compact.pop();
      }

      try {
        for (let ti = 0; ti < compact.length; ti++) {
          if (token.aborted) return;
          const tok = compact[ti];
          if (tok.kind === "space") {
            // We've already waited letterMs after the previous letter
            // committed. wordMs is the threshold for the space to be
            // appended; wait the difference + buffer.
            const remaining = T.wordMs - T.letterMs + COMMIT_BUFFER_MS;
            await wait(Math.max(0, remaining));
            continue;
          }
          const morse = tok.morse;
          for (let i = 0; i < morse.length; i++) {
            if (token.aborted) return;
            const sym = morse[i];
            const dur = sym === "." ? unit : unit * 3;
            handlePressDown(performance.now(), "manual");
            await wait(dur);
            if (token.aborted) { handlePressUp(performance.now()); return; }
            handlePressUp(performance.now());
            if (i < morse.length - 1) await wait(unit); // intra-element
          }
          // After a letter, wait long enough for letterMs to fire and
          // commit the letter to the transcription. Skip if this is
          // the last token (caller decides whether to wait for finalize).
          if (ti < compact.length - 1) {
            await wait(T.letterMs + COMMIT_BUFFER_MS);
          }
        }
      } finally {
        autoKeyAbortRef.current = null;
        setIsAutoKeying(false);
      }
    },
    [handlePressDown, handlePressUp]
  );

  // Cancel an in-progress auto-key sequence (for the Send-then-Stop UX).
  const stopAutoKey = useCallback(() => {
    const token = autoKeyAbortRef.current;
    if (token) {
      token.aborted = true;
      handlePressUp(performance.now());
    }
  }, [handlePressUp]);

  const clearCalibration = useCallback(() => {
    setDotDashOverride(null);
    setCalibResult(null);
    setCalDot(null);
    setCalDash(null);
  }, []);

  // Guided calibration: walk the user through "send a dot", then "send a
  // dash", capture both, and set the dot/dash threshold to their geometric
  // mean. Sensitivity is auto-set from the peak magnitude observed during
  // the dot step.
  const runCalibration = useCallback(async () => {
    if (calStep !== null || !micEnabled) return;
    setCalDot(null);
    setCalDash(null);
    setDotDashOverride(null);
    setCalibResult(null);

    // Phase 1 — dot
    setCalStep("dot");
    const dotRes = await audio.captureNextPulse(15000);
    if (!dotRes) {
      setCalStep(null);
      setCalibResult({ error: "No dot detected" });
      return;
    }
    setCalDot(Math.round(dotRes.duration));
    if (dotRes.peak > 0.0002) {
      const sens = Math.round(audio.sensitivityForThreshold(dotRes.peak * 0.4));
      sensUserTouchedRef.current = true;
      setSensitivity(sens);
    }

    // Phase 2 — dash
    setCalStep("dash");
    const dashRes = await audio.captureNextPulse(15000);
    if (!dashRes) {
      setCalStep(null);
      setCalibResult({
        error: "No dash detected",
        dot: Math.round(dotRes.duration),
      });
      return;
    }
    setCalDash(Math.round(dashRes.duration));

    const threshold = Math.round(
      Math.sqrt(dotRes.duration * dashRes.duration)
    );
    setDotDashOverride(threshold);
    setCalStep(null);
    setCalibResult({
      dot: Math.round(dotRes.duration),
      dash: Math.round(dashRes.duration),
      threshold,
      peak: dotRes.peak,
    });
  }, [audio, calStep, micEnabled]);

  const cancelCalibration = useCallback(() => {
    setCalStep(null);
  }, []);

  // When difficulty changes after practice has started, pick a fresh sentence
  // — except for CUSTOM mode where the user provides the text themselves.
  useEffect(() => {
    if (!practiceStarted) return;
    if (practiceDifficulty === CUSTOM_DIFFICULTY) {
      // Clear any randomly-generated sentence so the SEND button (which
      // is wired to practiceSentence) hides until the user types
      // something. Focus the custom input for immediate typing.
      setPracticeSentence("");
      requestAnimationFrame(() => customInputRef.current?.focus());
      return;
    }
    setPracticeSentence(randomSentence(practiceDifficulty));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [practiceDifficulty]);

  return (
    <div className="app-shell">
      {/* Controls bar */}
      <header className="controls-bar">
        <div className="brand">
          <Radio className="brand-icon" size={18} />
          <span className="brand-text">MORSE<span className="brand-dot">.</span>LAB</span>
        </div>

        <div className="controls-group">
          <ControlToggle
            label="Tree"
            checked={showTree}
            onChange={setShowTree}
            testId="toggle-tree"
          />
          <ControlToggle
            label="Letters"
            checked={showLetters}
            onChange={setShowLetters}
            testId="toggle-letters"
          />
          <ControlToggle
            label={audioEnabled ? "Audio" : "Muted"}
            checked={audioEnabled}
            onChange={(on) => {
              // Turning audio ON is a user gesture directly on the audio
              // control — the most reliable moment to unlock Web Audio on
              // iOS. Create/resume the context and play a silent buffer.
              if (on) audio.unlockAudio();
              setAudioEnabled(on);
            }}
            testId="toggle-audio"
            icon={audioEnabled ? <Volume2 size={14} /> : <VolumeX size={14} />}
          />

          <Popover>
            <PopoverTrigger asChild>
              <button
                className={`mic-trigger ${micEnabled ? "is-on" : ""}`}
                data-testid="mic-trigger"
                aria-label="Mic settings"
                title="Mic settings"
              >
                {micEnabled ? <Mic size={14} /> : <MicOff size={14} />}
                <span className="mic-trigger-label">MIC</span>
                <span className={`mic-trigger-status ${micEnabled ? "on" : "off"}`}>
                  {micEnabled ? "ON" : "OFF"}
                </span>
                {micEnabled && (
                  <span className="mic-trigger-meter" aria-hidden="true">
                    <span
                      className="mic-trigger-meter-fill"
                      style={{ width: `${Math.round(audio.micLevel * 100)}%` }}
                    />
                  </span>
                )}
              </button>
            </PopoverTrigger>
            <PopoverContent
              className="mic-popover"
              align="end"
              sideOffset={8}
              data-testid="mic-popover"
            >
              <div className="mic-popover-inner">
                <div className="mic-row">
                  <span className="mic-row-label">Listen</span>
                  <Switch
                    checked={micEnabled}
                    onCheckedChange={setMicEnabled}
                    data-testid="toggle-mic"
                  />
                </div>
                <div className="mic-row">
                  <span className={`mic-row-label ${!micEnabled ? "dim" : ""}`}>
                    Scribe to box
                  </span>
                  <Switch
                    checked={scribeEnabled}
                    onCheckedChange={setScribeEnabled}
                    disabled={!micEnabled}
                    data-testid="toggle-scribe"
                  />
                </div>
                <div className="mic-sens-block">
                  <div className="mic-sens-row">
                    <span className="mic-sens-label">Sensitivity</span>
                    <span className="mic-sens-value">{sensitivity}%</span>
                  </div>
                  <Slider
                    className="mic-sens-slider"
                    value={[sensitivity]}
                    min={0}
                    max={100}
                    step={1}
                    onValueChange={(v) => {
                      sensUserTouchedRef.current = true;
                      setSensitivity(Math.round(v[0]));
                    }}
                    data-testid="sensitivity-slider"
                  />
                  <div className="mic-sens-hint-row">
                    <button
                      type="button"
                      className="noise-recal-btn"
                      onClick={() => {
                        sensUserTouchedRef.current = false;
                        audio.measureNoiseFloor(900);
                      }}
                      disabled={!micEnabled}
                      data-testid="noise-recal-btn"
                      title="Re-measure ambient noise and auto-set sensitivity"
                    >
                      Auto from noise
                    </button>
                    {noiseFloorInfo && (
                      <span className="noise-info" data-testid="noise-info">
                        floor · {(noiseFloorInfo.p95 * 1000).toFixed(2)}‰
                      </span>
                    )}
                  </div>
                </div>
                <MicMeter
                  level={audio.micLevel}
                  magnitude={audio.micMagnitude}
                  threshold={audio.detectThreshold}
                  active={micEnabled}
                />
                <div className="calibrate-block">
                  <button
                    className="calibrate-btn"
                    onClick={runCalibration}
                    disabled={!micEnabled || calStep !== null}
                    data-testid="calibrate-btn"
                  >
                    {calStep === "dot"
                      ? "Send one dot…"
                      : calStep === "dash"
                        ? "Send one dash…"
                        : calDot !== null && calDash !== null
                          ? "Recalibrate"
                          : "Calibrate dot → dash"}
                  </button>
                  {calStep !== null && (
                    <button
                      className="cal-reset-btn"
                      onClick={cancelCalibration}
                      data-testid="cal-cancel-btn"
                    >
                      Cancel
                    </button>
                  )}
                  {calibResult && calStep === null && (
                    <div
                      className={`calibrate-result ${calibResult.error ? "warn" : ""}`}
                      data-testid="calibrate-result"
                    >
                      {calibResult.error ? (
                        <div>
                          {calibResult.error}
                          {calibResult.dot
                            ? ` (captured dot ${calibResult.dot} ms)`
                            : ""}
                        </div>
                      ) : (
                        <>
                          <div>
                            dot · {calibResult.dot} ms &nbsp;·&nbsp; dash ·{" "}
                            {calibResult.dash} ms
                          </div>
                          <div>
                            threshold · {calibResult.threshold} ms{" "}
                            <span className="cal-sub">(mic only)</span>
                          </div>
                        </>
                      )}
                    </div>
                  )}
                  {dotDashOverride !== null && calStep === null && (
                    <button
                      className="cal-reset-btn"
                      onClick={clearCalibration}
                      data-testid="cal-reset-btn"
                    >
                      Reset to WPM default
                    </button>
                  )}
                </div>
                <div className="mic-popover-hint">
                  raise sensitivity if levels look low · lower it in noisy rooms
                </div>
              </div>
            </PopoverContent>
          </Popover>

          <Popover>
            <PopoverTrigger asChild>
              <button
                className="tone-trigger"
                data-testid="speed-trigger"
                aria-label="Set keying speed"
                title="Keying speed (WPM)"
              >
                <Gauge size={14} className="trigger-icon" />
                <span className="tone-trigger-label">SPEED</span>
                <span className="tone-trigger-value">{wpm}</span>
                <span className="tone-trigger-unit">WPM</span>
              </button>
            </PopoverTrigger>
            <PopoverContent
              className="speed-popover"
              align="end"
              sideOffset={8}
              data-testid="speed-popover"
            >
              <div className="speed-popover-inner">
                <div className="speed-row">
                  <span className="speed-row-label">SPEED</span>
                  <span className="speed-row-value">{wpm} WPM</span>
                </div>
                <Slider
                  value={[wpm]}
                  min={5}
                  max={30}
                  step={1}
                  onValueChange={(v) => setWpm(Math.round(v[0]))}
                  data-testid="speed-slider"
                />
                <div className="speed-detail">
                  <span>unit · {Math.round(1200 / wpm)} ms</span>
                  <span>dash · {Math.round((1200 / wpm) * 3)} ms</span>
                  <span>dot/dash · {Math.round((1200 / wpm) * 2)} ms</span>
                </div>
                <div className="tone-popover-hint">
                  hold a tone longer than the dot/dash threshold to send a dash
                </div>
              </div>
            </PopoverContent>
          </Popover>

          <Popover>
            <PopoverTrigger asChild>
              <button
                className="tone-trigger"
                data-testid="tone-trigger"
                aria-label="Set tone frequency"
                title="Set tone frequency"
              >
                <span className="tone-trigger-label">TONE</span>
                <span className="tone-trigger-value">{frequency}</span>
                <span className="tone-trigger-unit">Hz</span>
              </button>
            </PopoverTrigger>
            <PopoverContent
              className="tone-popover"
              align="end"
              sideOffset={8}
              data-testid="tone-popover"
            >
              <div className="tone-popover-inner">
                <div className="tone-input-row">
                  <Input
                    id="freq-input"
                    type="number"
                    inputMode="numeric"
                    value={freqText}
                    onChange={handleFreqInputChange}
                    onBlur={commitFreqInput}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") {
                        e.preventDefault();
                        commitFreqInput();
                        e.currentTarget.blur();
                      }
                    }}
                    className="freq-input"
                    data-testid="freq-input"
                  />
                  <span className="freq-unit">Hz</span>
                </div>
                <SliderPrimitive.Root
                  className="vfreq-slider"
                  orientation="vertical"
                  value={[frequency]}
                  min={100}
                  max={2000}
                  step={1}
                  onValueChange={handleFreqSlider}
                  data-testid="freq-slider"
                >
                  <SliderPrimitive.Track className="vfreq-track">
                    <SliderPrimitive.Range className="vfreq-range" />
                  </SliderPrimitive.Track>
                  <SliderPrimitive.Thumb className="vfreq-thumb" />
                </SliderPrimitive.Root>
                <div className="tone-popover-hint">
                  drag · or type any value 50–6000
                </div>
              </div>
            </PopoverContent>
          </Popover>

          <button
            className="theme-toggle"
            onClick={() => setTheme((t) => (t === "dark" ? "light" : "dark"))}
            data-testid="toggle-theme"
            aria-label="Toggle theme"
            title="Toggle theme"
          >
            {theme === "dark" ? <Sun size={16} /> : <Moon size={16} />}
          </button>
        </div>
      </header>

      {/* Main area: tree left-justified, tap button right-centered */}
      <main className="main-area">
        <section className="tree-panel" data-testid="tree-panel">
          <div className="tree-area">
            {showTree ? (
              <MorseTree
                activePath={liveActivePath}
                showLetters={showLetters}
                className="tree-svg"
                onNodeClick={handleTreeNodeClick}
                disabled={isAutoKeying}
                currentMorse={currentMorse}
                calibratingFor={calStep}
                calDot={calDot}
                calDash={calDash}
              />
            ) : (
              <div className="tree-hidden">
                <span className="tree-hidden-morse" data-testid="live-morse">
                  {currentMorse || "—"}
                </span>
              </div>
            )}
          </div>

          <div className="practice-section" data-testid="practice-section">
            <div className="practice-controls">
              <button
                className="gen-button"
                onClick={handleGenerate}
                data-testid="gen-button"
                aria-label="Generate practice sentence"
              >
                <Sparkles size={14} />
                <span>{practiceStarted ? "NEW" : "GEN"}</span>
              </button>
              {practiceStarted && (
                <div className="difficulty-picker" data-testid="difficulty-picker">
                  <span className="diff-label">LEVEL</span>
                  <div className="diff-options" role="radiogroup">
                    {DIFFICULTY_LABELS.map((lbl, idx) => (
                      <button
                        key={lbl}
                        className={`diff-opt ${practiceDifficulty === idx ? "active" : ""}`}
                        onClick={() => setPracticeDifficulty(idx)}
                        data-testid={`diff-${idx}`}
                        role="radio"
                        aria-checked={practiceDifficulty === idx}
                      >
                        {lbl}
                      </button>
                    ))}
                  </div>
                </div>
              )}
              {/* Send / Stop button. Visible whenever there's content
                  to send: a generated sentence in EASY/MEDIUM/HARD, or
                  non-empty user-typed text in CUSTOM. */}
              {(() => {
                const customMode = practiceDifficulty === CUSTOM_DIFFICULTY;
                const hasContent = customMode
                  ? customText.trim().length > 0
                  : !!practiceSentence;
                if (!hasContent) return null;
                const sendCustom = async () => {
                  const txt = customText.trim();
                  if (!txt) return;
                  await keyTextSequence(txt);
                  // Clear the box once the transmission completes (or
                  // was aborted via STOP). Refocus so the user can type
                  // the next message immediately.
                  setCustomText("");
                  requestAnimationFrame(() => customInputRef.current?.focus());
                };
                const onClick = () => {
                  if (isAutoKeying) { stopAutoKey(); return; }
                  if (customMode) sendCustom();
                  else keyTextSequence(practiceSentence);
                };
                return (
                  <button
                    className="send-button"
                    onClick={onClick}
                    data-testid="send-button"
                    aria-label={isAutoKeying ? "Stop sending" : "Send message"}
                    title={
                      isAutoKeying
                        ? "Stop the in-progress transmission"
                        : "Auto-key the message at the current WPM"
                    }
                  >
                    {isAutoKeying ? "STOP" : "SEND"}
                  </button>
                );
              })()}
            </div>
            {practiceDifficulty === CUSTOM_DIFFICULTY && practiceStarted ? (
              <input
                ref={customInputRef}
                type="text"
                className="practice-sentence custom-input"
                value={customText}
                onChange={(e) => setCustomText(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    e.preventDefault();
                    if (isAutoKeying) { stopAutoKey(); return; }
                    const txt = customText.trim();
                    if (!txt) return;
                    keyTextSequence(txt).then(() => {
                      setCustomText("");
                      requestAnimationFrame(() => customInputRef.current?.focus());
                    });
                  }
                }}
                placeholder="Type a message and press Enter to send…"
                spellCheck={false}
                autoCapitalize="characters"
                data-testid="custom-input"
                aria-label="Custom message to transmit"
              />
            ) : (
              <div
                className={`practice-sentence ${practiceSentence ? "has-text" : ""}`}
                data-testid="practice-sentence"
              >
                {practiceSentence ||
                  "Press GEN for a practice sentence, or pick CUSTOM to send your own"}
              </div>
            )}
          </div>
        </section>

        <aside className="button-panel">
          <div className="button-stack">
            <TapButton
              isPressed={isPressed}
              onPressStart={() => handlePressDown()}
              onPressEnd={() => handlePressUp()}
            />
            <div className="press-hint" data-testid="press-hint">
              HOLD <kbd>SPACE</kbd> or tap
            </div>
            {micEnabled && (
              <div className="mic-meter" aria-hidden="true">
                <div
                  className="mic-meter-fill"
                  style={{ width: `${Math.round(audio.micLevel * 100)}%` }}
                />
              </div>
            )}
          </div>
        </aside>
      </main>

      {/* Transcription */}
      <footer className="transcribe-panel">
        <div className="transcribe-label-row">
          <Label className="transcribe-label" htmlFor="transcribe-box">Transcription</Label>
          <span className="current-morse" data-testid="current-morse">
            {currentMorse || "·"}
          </span>
          <div
            className="mode-picker"
            role="radiogroup"
            aria-label="Transcription mode"
            data-testid="mode-picker"
          >
            <button
              type="button"
              className={`mode-opt ${transcribeMode === "text" ? "active" : ""}`}
              onClick={() => setTranscribeMode("text")}
              role="radio"
              aria-checked={transcribeMode === "text"}
              data-testid="mode-text"
            >
              TEXT
            </button>
            <button
              type="button"
              className={`mode-opt ${transcribeMode === "morse" ? "active" : ""}`}
              onClick={() => setTranscribeMode("morse")}
              role="radio"
              aria-checked={transcribeMode === "morse"}
              data-testid="mode-morse"
            >
              · /&nbsp;–
            </button>
          </div>
          <Button
            variant="outline"
            className="clear-btn"
            onClick={() => {
              setTranscription("");
              setTranscriptionMorse("");
              setCurrentMorse("");
              currentMorseRef.current = "";
              clearTimers();
            }}
            data-testid="clear-btn"
          >
            Clear
          </Button>
        </div>
        <Textarea
          id="transcribe-box"
          className="transcribe-box"
          value={transcribeMode === "text" ? transcription : transcriptionMorse}
          onChange={(e) => {
            if (transcribeMode === "text") setTranscription(e.target.value);
            else setTranscriptionMorse(e.target.value);
          }}
          placeholder={
            transcribeMode === "text"
              ? "Your decoded message will appear here…"
              : "Raw morse stream (./–) will appear here…"
          }
          data-testid="transcription-textarea"
          rows={2}
          spellCheck={transcribeMode === "text"}
        />
      </footer>
    </div>
  );
}

function ControlToggle({ label, checked, onChange, testId, icon, className = "", disabled = false }) {
  return (
    <label
      className={`control-toggle ${className} ${disabled ? "is-disabled" : ""}`}
      data-testid={`${testId}-label`}
    >
      {icon && <span className="control-icon">{icon}</span>}
      <span className="control-label">{label}</span>
      <Switch
        checked={checked}
        onCheckedChange={onChange}
        disabled={disabled}
        data-testid={testId}
      />
    </label>
  );
}

function TapButton({ isPressed, onPressStart, onPressEnd }) {
  return (
    <button
      className={`tap-button ${isPressed ? "pressed" : ""}`}
      onMouseDown={(e) => { e.preventDefault(); onPressStart(); }}
      onMouseUp={(e) => { e.preventDefault(); onPressEnd(); }}
      onMouseLeave={() => { if (isPressed) onPressEnd(); }}
      onTouchStart={(e) => { e.preventDefault(); onPressStart(); }}
      onTouchEnd={(e) => { e.preventDefault(); onPressEnd(); }}
      data-testid="tap-button"
      aria-label="Tap to send morse"
    >
      <span className="tap-core" />
      <span className="tap-ring" />
      <span className="tap-text">TAP</span>
    </button>
  );
}

function MicMeter({ level, magnitude, threshold, active }) {
  // Visual scale: sqrt(mag/0.05) clamped to 0..1 (matches level)
  const SCALE = 0.05;
  const threshPct = Math.min(100, Math.sqrt(threshold / SCALE) * 100);
  const fillPct = Math.min(100, level * 100);
  const detecting = active && magnitude > threshold;
  return (
    <div className="mic-meter-block" data-testid="mic-meter-wide">
      <div className="mic-meter-row">
        <span className="mic-meter-caption">Level</span>
        <span className={`mic-meter-state ${detecting ? "on" : ""}`}>
          {detecting ? "TONE" : "—"}
        </span>
      </div>
      <div className="mic-meter-bar">
        <div
          className={`mic-meter-bar-fill ${detecting ? "detecting" : ""}`}
          style={{ width: `${fillPct}%` }}
        />
        <div
          className="mic-meter-bar-threshold"
          style={{ left: `${threshPct}%` }}
          aria-hidden="true"
        />
      </div>
    </div>
  );
}

